import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ApiService } from '@app/core/api/api.service';
import { ApiEndpoint, ApiFilterValues, ApiSearchValues } from '@app/core/models/api.models';
import { CompareValues, CustomerSummary, SubFilter } from '@app/core/models/common.models';
import { Currency, Customer } from '@app/core/models/customer.models';
import { formatNumber, isGermanLocale, isMonetaryValue } from '@app/core/services/metrics-calculations.service';
import { SubFiltersService } from '@app/core/services/subfilters.service';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { UserState } from '@app/core/store/states/user/user.state';
import { getPlatformLogo } from '@app/core/utilities/account.utilities';
import { filtersToApiValues } from '@app/core/utilities/api.utilities';
import { assertArray } from '@app/core/utilities/array.utilities';
import { microsToNumber } from '@app/core/utilities/number.utilities';
import { FilterTypes } from '@app/core/utilities/refiners.utilities';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { forkJoin, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, tap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'adspt-advanced-filter',
  templateUrl: './advanced-filter.component.html',
  styleUrls: ['./advanced-filter.component.scss'],
})
export class AdvancedFilterComponent implements OnInit, OnDestroy, OnChanges {
  private translate = inject(TranslateService);
  private subFiltersService = inject(SubFiltersService);
  private cd = inject(ChangeDetectorRef);
  private apiService = inject(ApiService);
  private store = inject(Store);
  protected readonly compareValuesArray = Object.values(CompareValues).map((compare) => ({
    key: compare,
    label: {
      [CompareValues.LE]: 'global-filter.sub-filters.is-less',
      [CompareValues.GE]: 'global-filter.sub-filters.is-greater',
      [CompareValues.EQUALS]: 'global-filter.sub-filters.is-equals',
      [CompareValues.RANGE]: 'global-filter.sub-filters.range',
    }[compare],
  }));

  filterTypes = FilterTypes;
  queryValue = '';
  queryMainFilterMenu: FormControl;
  isMainFilterMenuVisible: boolean;
  selectedFilters = [];
  filtersMenu = [];
  showSearch = false;
  processing = true;
  customer: Customer;
  summary: CustomerSummary;
  regex = isGermanLocale() ? /^-?(0|[1-9][0-9]*)(\,[0-9]*)?$/ : /^-?(0|[1-9][0-9]*)(\.[0-9]*)?$/;
  subscriptions$: Record<string, Subscription | undefined> = {};
  readonly translations = this.translate.instant('global-filter');
  @Input() defaultAPIparams?: any;
  @Input() source?:
    | 'account'
    | 'creation'
    | 'groups'
    | 'campaigns'
    | 'ad-groups'
    | 'criteria'
    | 'pg-wizard-select-campaigns'
    | 'destination-ad-groups';
  @Input() defaults: any = null;
  @Input() filters: any = null;
  @Input() columnTemplates = {};
  @Input() currency: Currency;

  @ViewChild('defaultPortfolioFilterTmpl')
  readonly defaultPortfolioFilterTmpl: TemplateRef<any>;

  @ViewChild('defaultLabelFilterTmpl')
  readonly defaultLabelFilterTmpl: TemplateRef<any>;

  get defaultFilterTemplates() {
    return {
      portfolio: this.defaultPortfolioFilterTmpl,
      label: this.defaultLabelFilterTmpl,
    };
  }

  @Output() filterChange = new EventEmitter<{
    filters: [string, string, string?][];
    apiFilters: ApiFilterValues;
    apiSearch: ApiSearchValues;
    param: string;
  }>();
  @ViewChild('inputElement', { static: false }) inputElement?: ElementRef;
  bufferForMultiSelect;
  tooltipMetricFilter = this.translate.instant('common.enter-number');

  trackByKey(index: number, item): string {
    return item.key;
  }

  onSwitchToggle(value, item) {
    item.value = value;
    item.isMenuVisible = false;
    this.emitFilter();
  }
  applyQuery(item) {
    item.value = this.queryValue;
    this.queryValue = '';
    item.isMenuVisible = false;
    this.emitFilter();
  }
  onSelectedFilterMenuChange(value: boolean, item) {
    if (value) {
      if (item.type === this.filterTypes.query) {
        this.queryValue = item.value || '';
        this._setQueryBoxFocus();
      } else if (item.type === this.filterTypes.queryMultiple) {
        this.queryValue = item.value || '';
        this._setQueryMultipleBoxFocus();
      } else if (item.type === this.filterTypes.multi) {
        this.bufferForMultiSelect = JSON.stringify(item.options);
      }
    } else {
      if (item.type === this.filterTypes.single && !item.options.filter((d) => d.selected).length) {
        // remove single-select filter if no option is selected
        this.removeSelectedFilter(item);
      }
      if (this.bufferForMultiSelect) {
        item.options = JSON.parse(this.bufferForMultiSelect);
        this.bufferForMultiSelect = null;
      }
    }
  }
  areAllChecked(options) {
    return options.filter((d) => !!d.selected).length === options.length;
  }
  isPartiallyChecked(options) {
    const tmpLength = options.filter((d) => !!d.selected).length;
    return tmpLength > 0 && tmpLength !== options.length;
  }
  updateAllChecked(value, item) {
    item.options = item.options.map((d) => {
      d.selected = value;
      return d;
    });
  }
  removeSelectedFilter(item) {
    setTimeout(() => {
      this.filtersMenu.forEach((d) => {
        if (d.key === item.key) {
          d.isAdded = false;
          if (item.type === this.filterTypes.multi || item.type === this.filterTypes.single) {
            d.options.forEach((e) => (e.selected = false));
          }
        }
      });
      this.selectedFilters = this.selectedFilters.filter((d) => d.key !== item.key);
      item.isMenuVisible = false;
      this.emitFilter();
    });
  }
  applyMultiSelectedFilter(item) {
    if (!item.options.filter((d) => d.selected === true).length) this.removeSelectedFilter(item);
    else {
      item.isMenuVisible = false;
      this.emitFilter();
    }
  }
  emitFilter() {
    const { filters, params } = this.subFiltersService.getQueryAndSelectedFilters(this.selectedFilters);
    this.filterChange.emit({
      filters: filters.map((d) => (d.type === this.filterTypes.compare ? [d.key, d.value, d.type] : [d.key, d.value])),
      apiFilters: filtersToApiValues(filters, 'filter'),
      apiSearch: filtersToApiValues(filters, 'search'),
      param: params.map((d) => `${d.key}:${d.value}`).join(';'),
    });
    this.closeMainFilterMenu();
    this.cd.detectChanges();
  }
  selectMultipleSubMenuItem(selectedItem, item) {
    selectedItem.selected = !selectedItem.selected;
    item.disabled = item.options.filter((d) => d.selected).length >= item.maxSelection;
  }
  selectSingleSubMenuItem(menuItem, item): void {
    item.options.forEach((d) => (d.selected = false));
    menuItem.selected = true;
    item.isMenuVisible = false;
    this.emitFilter();
  }
  closeMainFilterMenu(): void {
    this.isMainFilterMenuVisible = false;
    this.cd.markForCheck();
  }
  filterOptions(e, item) {
    if (!e.currentTarget.value) return (item.options = [...item.allOptions]);

    item.options = item.allOptions.filter(({ text }) => text.toLowerCase().includes(e.currentTarget.value));
  }
  onMenuItemSelected(item: SubFilter) {
    if (!this.isItemDisabled(item)) this._selectAndCloseMenu(item);
  }
  onMetricSelected(item) {
    item.isMenuVisible = false;
    this.emitFilter();
  }
  onBlur(item, element, forSecondValue = false): void {
    const key = forSecondValue ? 'endRange' : 'value';
    if (item?.[key]?.charAt(item[key].length - 1) === (isGermanLocale() ? ',' : '.') || item?.[key] === '-')
      this.updateMetricInputValue(item[key].slice(0, -1), item, element, forSecondValue);
  }
  updateMetricInputValue(value: string, item, element, forSecondValue = false): void {
    const key = forSecondValue ? 'endRange' : 'value';
    if ((!this.invalidNumber(value) && this.regex.test(value)) || value === '' || value === '-') item[key] = value;

    element.value = item[key] || '';
    const preffix = item.currency || '';
    const suffix = item.subType === 'percent' ? '%' : '';
    const localeAdjustValue = isGermanLocale() ? item[key]?.replace(',', '.') : item[key];
    this.tooltipMetricFilter = item[key] === '-' ? null : `${preffix} ${formatNumber(localeAdjustValue)}${suffix}`;
  }

  private invalidNumber(value: string) {
    return isGermanLocale() ? isNaN(+value.replace(',', '.')) : isNaN(+value);
  }
  private _setQueryBoxFocus() {
    setTimeout(() => {
      try {
        const ele: any = document.querySelector('.subfilter-query-box input');
        ele.focus();
        ele.select();
      } catch (error) {}
    });
  }

  private _setQueryMultipleBoxFocus() {
    setTimeout(() => {
      try {
        const ele: any = document.querySelector('adspt-input-tag input');
        ele.focus();
        ele.select();
      } catch (error) {}
    });
  }
  private _selectAndCloseMenu(item: SubFilter) {
    this.closeMainFilterMenu();
    item.isAdded = true;
    this.cd.detectChanges();
    setTimeout(() => {
      this.selectedFilters.push({
        ...item,
        isMenuVisible: true,
      });
      if (item.type === this.filterTypes.query) {
        item.value = '';
        this.queryValue = '';
        this._setQueryBoxFocus();
      } else if (item.type === this.filterTypes.queryMultiple) {
        item.value = '';
        this.queryValue = '';
        this._setQueryMultipleBoxFocus();
      } else if (item.type === this.filterTypes.switch) {
        item.value = !!item.value;
        this.selectedFilters[this.selectedFilters.length - 1].value = item.value;
        this.emitFilter();
      }
      this.cd.detectChanges();
    });
  }
  private _getData(item) {
    switch (item.key) {
      case 'tags':
        return this._getTags(item);
      case 'retailers':
        return this._getRetailers(item);
      case 'accounts':
        return this._getAccounts(item);
      case 'portfolio':
        return this._getPortfolios(item);
      case 'label':
        return this._getLabels(item);
      case 'pg':
        return this._getPGs(item);
    }
  }
  private _getRetailers(item) {
    return this.apiService
      .getList({
        limit: 1000,
        route: item.optionsUri.route,
        endpoint: item.optionsUri.api,
        sortValues: { updatedAt: 1 } as any,

        include: ['name', 'retailerId'],
      })
      .pipe(
        tap((res) => {
          item.allOptions = res.data.map((d) => ({
            text: d.name,
            key: d.retailerId,
            selected: false,
          }));
          item.options = [...item.allOptions];
        }),
      );
  }
  private _getTags(item) {
    const filterValues: any = {};
    if (!!this.customer.customerId) filterValues.customerId = this.customer.customerId;

    if (this.store.selectSnapshot(UserState.isSysAdmin)) filterValues.tagScope = 'USER';

    return this.apiService
      .getList({
        limit: 1000,
        route: item.optionsUri.route,
        endpoint: item.optionsUri.api,
        include: ['tagName'],
        sortValues: { tagName: 1 } as any,
        filterValues,
      })
      .pipe(
        tap((res) => {
          item.allOptions = res.data
            .filter((v, i, a) => a.findIndex((t) => t.tagName === v.tagName) === i)
            .map((d) => ({
              text: d.tagName,
              key: d.tagName,
              selected: false,
            }));
          item.options = [...item.allOptions];
        }),
      );
  }
  private _getAccounts(item) {
    const filterValues: any = {};
    if (!!this.customer.customerId) {
      filterValues.customerId = this.customer.customerId;
    }
    return this.apiService
      .getList({
        limit: 1000,
        route: item.optionsUri.route,
        endpoint: item.optionsUri.api,
        include: ['adspertAccountId', 'accountName', 'platform', 'accountId'],
        sortValues: { accountName: 1 } as any,
        filterValues,
      })
      .pipe(
        tap((res) => {
          item.allOptions = res.data
            .filter((v, i, a) => a.findIndex((t) => t.adspertAccountId === v.adspertAccountId) === i)
            .map((d) => ({
              text: d.accountName,
              key: d.adspertAccountId,
              id: d.accountId,
              logoUrl: getPlatformLogo(d.platform),
              selected: false,
            }));
          item.options = [...item.allOptions];
        }),
      );
  }

  private _getPGs(item) {
    return this.apiService
      .getList({
        limit: 1000,
        route: [this.customer.customerId, item.optionsUri.route],
        endpoint: item.optionsUri.api,
        include: ['performanceGroupId', 'name', 'campaigns', 'goals', 'pgCurrency'],
        sortValues: { name: 1 } as any,
        params: this.defaultAPIparams?.pg ?? {},
      })
      .pipe(
        tap((res) => {
          item.allOptions = res.data
            .filter((v, i, a) => a.findIndex((t) => t.performanceGroupId === v.performanceGroupId) === i)
            .map((d) => ({
              text: d.name,
              key: d.performanceGroupId,
              pgCurrency: d.pgCurrency,
              count: d.campaigns.length,
              goals: d.goals.map((goal) => {
                if (isMonetaryValue(goal.goalType) && !isNaN(goal.goalValue))
                  goal.goalValue = microsToNumber(goal.goalValue);
                return goal;
              }),
              selected: false,
            }));
          item.options = [...item.allOptions];
        }),
      );
  }
  private _getLabels(item) {
    return this.apiService
      .getList({
        limit: 1000,
        route: [ApiEndpoint.Customers, this.customer.customerId, item.optionsUri.route],
        endpoint: item.optionsUri.api,
        include: ['labelName'],
        params: {
          label_level: 'CAMPAIGN',
        },
        sortValues: { labelName: 1 } as any,
      })
      .pipe(
        tap((res) => {
          item.allOptions = res.data
            .filter((v, i, a) => a.findIndex((t) => t.labelName === v.labelName) === i)
            .map((d) => ({
              text: d.labelName,
              key: d.labelName,
              selected: false,
            }));
          item.options = [...item.allOptions];
        }),
        catchError((_, caught) => {
          item.allOptions = [];
          item.options = [];
          return caught;
        }),
      );
  }
  private _getPortfolios(item) {
    return this.apiService
      .getList({
        limit: 1000,
        route: [this.customer.customerId, item.optionsUri.route],
        endpoint: item.optionsUri.api,
        include: ['adspertPortfolioId', 'portfolioName', 'accountName'],
        sortValues: { portfolioName: 1 } as any,
      })
      .pipe(
        tap((res) => {
          item.allOptions = res.data
            .filter((v, i, a) => a.findIndex((t) => t.adspertPortfolioId === v.adspertPortfolioId) === i)
            .map((d) => ({
              accountName: d.accountName,
              text: d.portfolioName,
              key: d.adspertPortfolioId,
              selected: false,
            }));
          item.options = [...item.allOptions];
        }),
      );
  }
  private _setDefaultsAndEmit() {
    if (this.defaults) {
      this.selectedFilters = this.subFiltersService
        .getSelectedFilters(this.defaults, this.filtersMenu)
        .filter((d) => !!d)
        .map((d) => {
          d.isMenuVisible = false;
          return d;
        });
    }
    this.processing = false;
    this.emitFilter();
    this.cd.markForCheck();
  }

  isItemDisabled(item: SubFilter): boolean {
    return item.disabled || ((item.type === 'multi-select' || item.type === 'single-select') && !item.options?.length);
  }

  setFiltersMenu(): void {
    this.filtersMenu = [
      ...assertArray(
        this.filters.map((d) => {
          delete d.isAdded;
          d?.allOptions?.forEach((e) => (e.selected = false));
          d?.options?.forEach((e) => (e.selected = false));
          return d;
        }),
      ),
    ];

    this.showSearch = this.filtersMenu.length > 7;
    if (this.filtersMenu.filter((d) => d.optionsUri).length) {
      // some of the filters needs to fetch data from API wait until that is finished
      this.subscriptions$.optionsData = forkJoin([
        ...this.filtersMenu.filter((d) => d.optionsUri).map((item) => this._getData.apply(this, [item])),
      ]).subscribe(this._setDefaultsAndEmit.bind(this));
    } else this._setDefaultsAndEmit();
  }

  constructor() {
    this.customer = this.store.selectSnapshot(CustomerState.customer);
    this.summary = this.store.selectSnapshot(CustomerState.summary);
    this.queryMainFilterMenu = new FormControl();
    this.subscriptions$.queryMainFilter = this.queryMainFilterMenu.valueChanges
      .pipe(
        filter((value) => value !== undefined && value !== null),
        distinctUntilChanged(),
      )
      .subscribe(
        (value) => (this.filtersMenu = this.filters.filter((d) => d.displayName.toLowerCase().indexOf(value) !== -1)),
      );
  }
  ngOnInit() {
    this.setFiltersMenu();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filters && !changes.filters.firstChange) {
      this.setFiltersMenu();
    }
  }

  ngOnDestroy() {
    Object.keys(this.subscriptions$).forEach((key) => this.subscriptions$[key]?.unsubscribe());
  }
}
